/**
 * EP-99 T6 — WIN pickup relay operator settings.
 *
 * Dual-gate: Unleash flag `win_pickup` AND `accountSettings.settings.win_pickup_enabled`
 * (the SettingsPage tab gate combines both).
 *
 * Renders inside SettingsPage as a dedicated tab when the module is enabled.
 * Admin/owner only (BE enforces; FE hides upload & rotate from staff too).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RotateCw, Copy, Check } from 'lucide-react'
import { Card, Button, Input, Alert, Badge, Skeleton, FileInput } from '@/components/ui'
import { api } from '@/lib/api'
import {
  useWinPickupCredentials,
  useWinPickupBindCode,
  useUpdateWinPickupCredentials,
  useRotateBindCode,
  useUpdateWinPickupFareBoard,
} from '@/hooks/useWinPickupSettings'
import { useNotificationStore } from '@/stores/useNotificationStore'

const API_BASE = import.meta.env.VITE_API_URL || ''

function resolveApiUrl(url: string): string {
  return url.startsWith('/api/') ? `${API_BASE}${url}` : url
}

interface WinPickupSettingsSectionProps {
  canEdit: boolean
}

// ---- Bind status chip ------------------------------------------------------

function BindStatusBadge({ status }: { status: 'pending_bind' | 'active' | 'inactive' }) {
  const { t } = useTranslation('settings')
  const variantMap = {
    pending_bind: 'warning',
    active: 'success',
    inactive: 'ghost',
  } as const

  return (
    <Badge variant={variantMap[status]}>
      {t(`win_pickup.bind_status.${status}`)}
    </Badge>
  )
}

// ---- Main component --------------------------------------------------------

export function WinPickupSettingsSection({ canEdit }: WinPickupSettingsSectionProps) {
  const { t } = useTranslation('settings')
  const addToast = useNotificationStore((s) => s.addToast)

  // --- Credentials form state ---
  const { data: creds, isLoading: credsLoading } = useWinPickupCredentials()
  const updateCreds = useUpdateWinPickupCredentials()

  const [channelSecret, setChannelSecret] = useState('')
  const [accessToken, setAccessToken] = useState('')

  const handleSaveCreds = async (e: React.FormEvent) => {
    e.preventDefault()
    const params: { line_channel_secret?: string; line_channel_access_token?: string } = {}
    if (channelSecret.trim()) params.line_channel_secret = channelSecret.trim()
    if (accessToken.trim()) params.line_channel_access_token = accessToken.trim()
    if (!params.line_channel_secret && !params.line_channel_access_token) return

    try {
      await updateCreds.mutateAsync(params)
      setChannelSecret('')
      setAccessToken('')
      addToast({ type: 'success', message: t('win_pickup.creds_saved') })
    } catch {
      addToast({ type: 'error', message: t('win_pickup.creds_error') })
    }
  }

  // --- Bind code ---
  const { data: bindCodeData, isLoading: bindLoading } = useWinPickupBindCode()
  const rotateBindCode = useRotateBindCode()
  const [codeCopied, setCodeCopied] = useState(false)

  const handleCopyBindCode = async () => {
    if (!bindCodeData?.bind_code) return
    await navigator.clipboard.writeText(bindCodeData.bind_code)
    setCodeCopied(true)
    setTimeout(() => setCodeCopied(false), 2000)
  }

  const handleRotate = async () => {
    try {
      await rotateBindCode.mutateAsync()
      addToast({ type: 'success', message: t('win_pickup.bind_code_rotated') })
    } catch {
      addToast({ type: 'error', message: t('win_pickup.bind_code_rotate_error') })
    }
  }

  // --- Fare-board image upload ---
  const updateFareBoard = useUpdateWinPickupFareBoard()
  const [fareBoardUrl, setFareBoardUrl] = useState<string | null>(null)
  const [isFareBoardUploading, setIsFareBoardUploading] = useState(false)

  const handleFareBoardChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsFareBoardUploading(true)
    try {
      const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
        filename: file.name,
        content_type: file.type,
      })
      await fetch(presigned.url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      })
      const result = await updateFareBoard.mutateAsync(presigned.key)
      setFareBoardUrl(result.fare_board_url)
      addToast({ type: 'success', message: t('win_pickup.fare_board_saved') })
    } catch {
      addToast({ type: 'error', message: t('win_pickup.fare_board_error') })
    } finally {
      setIsFareBoardUploading(false)
      // Reset file input so the same file can be re-uploaded if needed.
      e.target.value = ''
    }
  }

  // ---- Render ----------------------------------------------------------------

  if (credsLoading || bindLoading) {
    return (
      <div className="space-y-6">
        <Card><Skeleton className="h-48" /></Card>
        <Card><Skeleton className="h-32" /></Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Section description */}
      <Alert variant="info" icon={null}>
        {t('win_pickup.description')}
      </Alert>

      {/* 1. Bind status + bind code */}
      <Card>
        {/* The status chip sits on the title line, not mid-way down the hint
            it used to be centred against (phone, 2026-10-01). */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="font-semibold">{t('win_pickup.bind_code_title')}</h3>
            <p className="text-sm text-base-content/60 mt-0.5">{t('win_pickup.bind_code_hint')}</p>
          </div>
          {bindCodeData && <div className="shrink-0"><BindStatusBadge status={bindCodeData.status} /></div>}
        </div>

        {bindCodeData && (
          <div className="space-y-3">
            {/* Read-only code field + copy button */}
            <div className="flex items-end gap-2 min-w-0">
              <div className="flex-1 min-w-0">
                <Input
                  label={t('win_pickup.bind_code_label')}
                  value={bindCodeData.bind_code}
                  readOnly
                  aria-readonly="true"
                  className="font-mono"
                />
              </div>
              {/* Default size: the same height as the input it sits beside. */}
              <Button
                type="button"
                variant="outline"
                leftIcon={codeCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                onClick={handleCopyBindCode}
                aria-label={t('win_pickup.copy_bind_code')}
              >
                {codeCopied ? t('win_pickup.copied') : t('win_pickup.copy')}
              </Button>
            </div>

            {/* Rotate button — admin/owner only */}
            {canEdit && (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  leftIcon={<RotateCw className="w-4 h-4" />}
                  loading={rotateBindCode.isPending}
                  onClick={handleRotate}
                  aria-label={t('win_pickup.rotate_bind_code')}
                  data-testid="win-pickup-rotate-bind-code"
                >
                  {t('win_pickup.rotate_bind_code')}
                </Button>
                <p className="text-xs text-base-content/60 mt-1">{t('win_pickup.rotate_bind_code_hint')}</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 2. LINE OA credentials */}
      <Card>
        <h3 className="font-semibold mb-1">{t('win_pickup.credentials_title')}</h3>
        <p className="text-sm text-base-content/60 mb-4">{t('win_pickup.credentials_description')}</p>

        <form onSubmit={handleSaveCreds}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="min-w-0">
              <Input
                label={t('win_pickup.channel_secret')}
                name="line_channel_secret"
                type="password"
                value={channelSecret}
                onChange={(e) => setChannelSecret(e.target.value)}
                placeholder={
                  creds?.line_channel_secret_present
                    ? t('win_pickup.channel_secret_configured')
                    : t('win_pickup.channel_secret_placeholder')
                }
                disabled={!canEdit}
                autoComplete="off"
              />
              <p className="text-xs text-base-content/60 mt-1">{t('win_pickup.channel_secret_hint')}</p>
            </div>

            <div className="min-w-0">
              <Input
                label={t('win_pickup.access_token')}
                name="line_channel_access_token"
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={
                  creds?.line_channel_access_token_present
                    ? t('win_pickup.access_token_configured')
                    : t('win_pickup.access_token_placeholder')
                }
                disabled={!canEdit}
                autoComplete="off"
              />
              <p className="text-xs text-base-content/60 mt-1">{t('win_pickup.access_token_hint')}</p>
            </div>
          </div>

          {canEdit && (
            <div className="flex justify-end mt-4">
              <Button
                type="submit"
                loading={updateCreds.isPending}
                disabled={!channelSecret.trim() && !accessToken.trim()}
                data-testid="win-pickup-save-creds"
              >
                {t('win_pickup.save_credentials')}
              </Button>
            </div>
          )}
        </form>
      </Card>

      {/* 3. Fare-board image */}
      <Card>
        <h3 className="font-semibold mb-1">{t('win_pickup.fare_board_title')}</h3>
        <p className="text-sm text-base-content/60 mb-4">{t('win_pickup.fare_board_description')}</p>

        {fareBoardUrl && (
          <div className="mb-4">
            <img
              src={resolveApiUrl(fareBoardUrl)}
              alt={t('win_pickup.fare_board_preview_alt')}
              className="max-w-xs w-full h-auto object-contain border rounded"
            />
          </div>
        )}

        {canEdit && (
          <div>
            <FileInput
              accept="image/*"
              size="sm"
              className="max-w-xs"
              onChange={handleFareBoardChange}
              disabled={isFareBoardUploading}
              aria-label={t('win_pickup.fare_board_upload_label')}
              data-testid="win-pickup-fare-board-input"
            />
            {isFareBoardUploading && (
              <p className="text-sm text-base-content/60 mt-2">{t('win_pickup.fare_board_uploading')}</p>
            )}
            <p className="text-xs text-base-content/60 mt-1">{t('win_pickup.fare_board_hint')}</p>
          </div>
        )}
      </Card>
    </div>
  )
}
